import { ThreadArtifactItem } from './ThreadRunPane/collectThreadArtifacts'

/**
 * Search-param open for the artifacts column.
 *
 * Accepts living `publicId` (UUID) and legacy `stepId:slug:pos`.
 * Cache scope: none — one-shot URL state, not an LLM prompt.
 */
export const THREAD_ARTIFACT_SEARCH_PARAM = 'artifact'

export function readThreadArtifactKey(
  searchParams: URLSearchParams,
): string | null {
  const value = searchParams.get(THREAD_ARTIFACT_SEARCH_PARAM)?.trim()
  if (!value) {
    return null
  }

  return value
}

export function writeThreadArtifactSearchParam(
  searchParams: URLSearchParams,
  key: string | null,
): URLSearchParams {
  const next = new URLSearchParams(searchParams)
  if (key) {
    next.set(THREAD_ARTIFACT_SEARCH_PARAM, key)
  } else {
    next.delete(THREAD_ARTIFACT_SEARCH_PARAM)
  }

  return next
}

/**
 * Parse `stepId:slug:pos` (legacy collector / `?artifact=` key).
 * Slug may contain colons; position is the final integer segment.
 */
export function parseLegacyArtifactIdentity(
  identity: string,
): { stepId: string; slug: string; position: number } | null {
  const firstColon = identity.indexOf(':')
  if (firstColon <= 0) {
    return null
  }
  const lastColon = identity.lastIndexOf(':')
  if (lastColon <= firstColon) {
    return null
  }
  const stepId = identity.slice(0, firstColon)
  const slug = identity.slice(firstColon + 1, lastColon)
  const positionRaw = identity.slice(lastColon + 1)
  if (!stepId || !slug || positionRaw === '') {
    return null
  }
  const position = Number(positionRaw)
  if (!Number.isInteger(position) || position < 0) {
    return null
  }
  return { stepId, slug, position }
}

/**
 * Resolve `?artifact=` against the living rail: publicId first, then legacy
 * `stepId:slug:pos` via promote identity (`stepId` / `sourceStepId`), never
 * treating `position` as a global list index.
 */
export function findThreadArtifactBySearchKey(
  items: Array<ThreadArtifactItem>,
  key: string,
): ThreadArtifactItem | null {
  const trimmed = key.trim()
  if (!trimmed) {
    return null
  }

  const byPublicId = items.find(
    (item) => item.publicId === trimmed || item.key === trimmed,
  )
  if (byPublicId) {
    return byPublicId
  }

  const parsed = parseLegacyArtifactIdentity(trimmed)
  if (!parsed) {
    return null
  }

  const byPromoteKey = items.find((item) => {
    const stepMatch =
      item.stepId === parsed.stepId ||
      item.sourceStepId === parsed.stepId ||
      item.key.startsWith(`${parsed.stepId}:`)
    if (!stepMatch) {
      return false
    }
    return item.slug === parsed.slug
  })
  if (byPromoteKey) {
    return byPromoteKey
  }

  const sameSlug = items.filter((item) => item.slug === parsed.slug)
  if (sameSlug.length === 0) {
    return null
  }
  if (sameSlug.length === 1) {
    return sameSlug[0] ?? null
  }
  const byFanoutIndex = sameSlug.find(
    (item) => item.index === parsed.position,
  )
  return byFanoutIndex ?? sameSlug[0] ?? null
}

/**
 * Fullscreen / expanded Maximize lookup. Living rail items first, then
 * pending creative drafts (`publicId ?? key`) that are not yet in the rail.
 */
export function findExpandableThreadArtifact(
  runArtifacts: Array<ThreadArtifactItem>,
  draftItems: Array<ThreadArtifactItem>,
  key: string,
): ThreadArtifactItem | null {
  return (
    findThreadArtifactBySearchKey(runArtifacts, key) ??
    findThreadArtifactBySearchKey(draftItems, key)
  )
}
